
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ProviderGalleryImage {
  id: string;
  provider_id: string;
  image_url: string;
  alt_text?: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useProviderGallery = (providerId?: string) => {
  return useQuery({
    queryKey: ['provider-gallery', providerId],
    queryFn: async () => {
      if (!providerId) return [];
      
      const { data, error } = await supabase
        .from('provider_gallery')
        .select('*')
        .eq('provider_id', providerId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching provider gallery:', error);
        throw error;
      }

      return data as ProviderGalleryImage[];
    },
    enabled: !!providerId,
  });
};

export const useAddGalleryImage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (imageData: {
      provider_id: string;
      image_url: string;
      alt_text?: string;
      display_order?: number;
    }) => {
      const { data, error } = await supabase
        .from('provider_gallery')
        .insert(imageData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['provider-gallery', data.provider_id] });
      toast({
        title: "Image Added",
        description: "Gallery image has been added successfully.",
      });
    },
    onError: (error) => {
      console.error('Error adding gallery image:', error);
      toast({
        title: "Error",
        description: "Failed to add gallery image. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateGalleryImage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<ProviderGalleryImage> & { id: string }) => {
      const { data, error } = await supabase
        .from('provider_gallery')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['provider-gallery', data.provider_id] });
      toast({
        title: "Image Updated",
        description: "Gallery image has been updated successfully.",
      });
    },
    onError: (error) => {
      console.error('Error updating gallery image:', error);
      toast({
        title: "Error",
        description: "Failed to update gallery image. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteGalleryImage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, provider_id }: { id: string; provider_id: string }) => {
      const { error } = await supabase
        .from('provider_gallery')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
      return { id, provider_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['provider-gallery', data.provider_id] });
      toast({
        title: "Image Removed",
        description: "Gallery image has been removed successfully.",
      });
    },
    onError: (error) => {
      console.error('Error removing gallery image:', error);
      toast({
        title: "Error",
        description: "Failed to remove gallery image. Please try again.",
        variant: "destructive",
      });
    },
  });
};
